import { useState, useRef, useEffect } from 'react';
import { getSessionPauseFlag } from './DeerFlow/useDeerFlow';
import { setUserStoppedState } from './DeerFlow/useReportUtils';
import { handleDeerFlowStop as apiHandleDeerFlowStop } from '../../api/grt/ai';

/**
 * useWebSender Hook - 管理 WebSender 组件的暂停图标、状态和事件处理
 * 
 * @param {Object} params - Hook 参数
 * @param {string} params.sessionId - 当前会话 ID
 * @param {boolean} params.isDeerFlow - 是否为 DeerFlow 技能
 * @param {boolean} params.isAippt - 是否为 AIPPT 技能
 * @param {Array} params.messages - 消息列表
 * @param {string} params.activeReportId - 当前活跃的报告 ID
 * (DeerFlow 停止函数已从 API 引入，不再作为参数传入)
 * @param {Function} params.stopHistoryPolling - 停止历史记录轮询函数
 * @param {Function} params.onStop - 停止回调函数
 * @param {Function} params.setUserStoppedState - 设置用户停止状态函数
 * 
 * @returns {Object} 返回状态和方法
 */
export const useWebSender = ({
    sessionId,
    isDeerFlow,
    isAippt,
    messages,
    activeReportId,
    stopHistoryPolling,
    onStop
}) => {
    // 🔥 状态管理
    const [showPauseIcon, setShowPauseIcon] = useState(false);
    const [isResearchTask, setIsResearchTask] = useState(false);
    const [isPauseClickable, setIsPauseClickable] = useState(false);
    const [aipptLoading, setAipptLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    // 🔥 Refs
    const lastSubmitTimeRef = useRef(0);
    const submitTimeoutRef = useRef(null);

    // 🔥 监听任务完成事件，隐藏暂停图标
    useEffect(() => {
        const handleTaskCompleted = (event) => {
            const { sessionId: completedSessionId } = event.detail;
            // 只处理当前会话的完成事件
            if (completedSessionId === sessionId) {
                // 🔥 新增：通知useChat.js更新loading状态
                window.dispatchEvent(
                    new CustomEvent('session-loading-update', {
                        detail: {
                            sessionId: completedSessionId,
                            loading: false,
                            answering: false,
                            timestamp: Date.now()
                        }
                    })
                );
                setShowPauseIcon(false);
                setIsResearchTask(false); // 🔥 重置研究任务标记
                setAipptLoading(false); // 🔥 重置AIPPT加载状态
            }
        };

        // 🔥 监听研究计划开始事件，统一更新暂停状态（避免重复存储）
        const handleResearchPlanStarted = (event) => {
            const { planId, sessionId, timestamp } = event.detail;
            // 只有在DeerFlow模式下且事件的sessionId匹配当前sessionId时才处理
            // Use string comparison to avoid type mismatch (number vs string)
            if (isDeerFlow && sessionId) {
                setShowPauseIcon(true);
                setIsResearchTask(true); // 🔥 标记为研究任务
                setIsPauseClickable(true); // 🔥 设置暂停按钮可点击

                // 🔥 统一在这里更新 localStorage（避免重复存储）
                try {
                    const historySessions = JSON.parse(localStorage.getItem('grt_history_sessions') || '[]');
                    const sessionIndex = historySessions.findIndex((item) =>
                        typeof item === 'object' ? item.sessionId === sessionId : item === sessionId
                    );
                    if (sessionIndex !== -1 && typeof historySessions[sessionIndex] === 'object') {
                        historySessions[sessionIndex].isClickable = true; // 🔥 设置为可点击
                        historySessions[sessionIndex].canPause = true;
                        localStorage.setItem('grt_history_sessions', JSON.stringify(historySessions));
                    }
                } catch (error) {
                    console.error('🔥 更新 isClickable 状态失败:', error);
                }
            }
        };

        // 🔥 新增：监听AIPPT加载开始事件
        const handleAipptLoadingStart = (event) => {
            const { sessionId: loadingSessionId, type } = event.detail || {};
            // 🔥 修复：更灵活的sessionId匹配逻辑
            const effectiveSessionId = sessionId || localStorage.getItem('aippt_prev_session_id');
            const shouldHandle =
                !loadingSessionId || // 没有传递sessionId，默认处理
                loadingSessionId === effectiveSessionId || // 精确匹配当前会话
                loadingSessionId === sessionId || // 匹配props中的sessionId
                (effectiveSessionId && loadingSessionId === effectiveSessionId); // 匹配localStorage中的sessionId

            if (shouldHandle) {
                setAipptLoading(true);
                setShowPauseIcon(true); // 显示暂停图标
            }
        };

        // 🔥 新增：监听AIPPT加载完成事件
        const handleAipptLoadingComplete = (event) => {
            const { sessionId: loadingSessionId, type } = event.detail || {};
            // 🔥 修复：更灵活的sessionId匹配逻辑
            const effectiveSessionId = sessionId || localStorage.getItem('aippt_prev_session_id');
            const shouldHandle =
                !loadingSessionId || // 没有传递sessionId，默认处理
                loadingSessionId === effectiveSessionId || // 精确匹配当前会话
                loadingSessionId === sessionId || // 匹配props中的sessionId
                (effectiveSessionId && loadingSessionId === effectiveSessionId); // 匹配localStorage中的sessionId

            if (shouldHandle) {
                setAipptLoading(false);
                setShowPauseIcon(false); // 隐藏暂停图标
            }
        };

        window.addEventListener('deerflow-task-completed', handleTaskCompleted);
        window.addEventListener('research-plan-started', handleResearchPlanStarted);
        window.addEventListener('aippt-loading-start', handleAipptLoadingStart);
        window.addEventListener('aippt-loading-complete', handleAipptLoadingComplete);

        return () => {
            window.removeEventListener('deerflow-task-completed', handleTaskCompleted);
            window.removeEventListener('research-plan-started', handleResearchPlanStarted);
            window.removeEventListener('aippt-loading-start', handleAipptLoadingStart);
            window.removeEventListener('aippt-loading-complete', handleAipptLoadingComplete);
        };
    }, [sessionId, isDeerFlow]);

    // 🔥 修复：sessionId变化时智能检查暂停状态
    useEffect(() => {
        if (sessionId && isDeerFlow) {
            const pauseFlag = getSessionPauseFlag(sessionId);

            // 🔥 新增：同时读取isClickable状态
            let clickableFlag = false;
            try {
                const historySessions = JSON.parse(localStorage.getItem('grt_history_sessions') || '[]');
                const sessionData = historySessions.find((item) => (typeof item === 'object' ? item.sessionId === sessionId : item === sessionId));
                clickableFlag = sessionData?.isClickable || false;
            } catch (error) {
                console.error('🔥 读取isClickable状态失败:', error);
            }

            // 🔥 修复：如果pauseFlag为true，但消息中没有正在进行的任务，应该检查并更新状态
            if (pauseFlag && messages && Array.isArray(messages) && messages.length > 0) {
                // 检查消息中是否真的有正在进行的任务（包括普通chat和DeerFlow任务）
                const hasLoadingMessage = messages.some(
                    (msg) =>
                        msg.status === 'loading' ||
                        msg.status === 'answering' ||
                        msg.question?.status === 'loading' ||
                        msg.question?.status === 'answering'
                );

                const hasOngoingDeerFlowTask = messages.some((msg) => {
                    // 检查是否有报告卡片但没有完成
                    if (msg.isReportMessage || (msg.content && msg.content.includes('data-type="report-notice"'))) {
                        // 检查该报告是否已完成（通过检查后续是否有reporter消息）
                        const msgIndex = messages.indexOf(msg);
                        const hasReporterAfter = messages
                            .slice(msgIndex + 1)
                            .some((laterMsg) => laterMsg.name === 'reporter' && laterMsg.content && laterMsg.content.trim() !== '');
                        return !hasReporterAfter; // 如果没有reporter消息，说明任务还在进行
                    }
                    return false;
                });

                // 如果没有正在进行的任务，应该清除pauseFlag
                if (!hasLoadingMessage && !hasOngoingDeerFlowTask) {
                    try {
                        const historySessions = JSON.parse(localStorage.getItem('grt_history_sessions') || '[]');
                        const sessionIndex = historySessions.findIndex((session) =>
                            typeof session === 'object' ? session.sessionId === sessionId : session === sessionId
                        );

                        if (sessionIndex >= 0 && typeof historySessions[sessionIndex] === 'object') {
                            historySessions[sessionIndex].canPause = false;
                            historySessions[sessionIndex].isClickable = false;
                            localStorage.setItem('grt_history_sessions', JSON.stringify(historySessions));

                            // 更新本地状态
                            setShowPauseIcon(false);
                            setIsPauseClickable(false);
                            setIsResearchTask(false);
                            return; // 提前返回，不执行后续的状态设置
                        }
                    } catch (error) {
                        console.error('🔥 会话切换时更新状态失败:', error);
                    }
                }
                // 🔥 新增：如果有正在进行的报告生成任务，但isClickable为false，则自动修复
                else if (hasOngoingDeerFlowTask && !clickableFlag) {
                    try {
                        const historySessions = JSON.parse(localStorage.getItem('grt_history_sessions') || '[]');
                        const sessionIndex = historySessions.findIndex((session) =>
                            typeof session === 'object' ? session.sessionId === sessionId : session === sessionId
                        );

                        if (sessionIndex >= 0 && typeof historySessions[sessionIndex] === 'object') {
                            historySessions[sessionIndex].isClickable = true; // 🔥 修复：设置为可点击
                            localStorage.setItem('grt_history_sessions', JSON.stringify(historySessions));
                            clickableFlag = true; // 更新本地变量，确保后续使用最新值
                        }
                    } catch (error) {
                        console.error('🔥 自动修复isClickable状态失败:', error);
                    }
                }
            }

            setShowPauseIcon(pauseFlag);
            setIsPauseClickable(clickableFlag); // 🔥 设置暂停按钮可点击状态
            // 🔥 会话切换时重置研究任务标记，需要重新判断
            setIsResearchTask(false);
        } else if (sessionId && isAippt) {
            // 🔥 新增：AIPPT模式下检查是否有加载中的消息
            const effectiveSessionId = sessionId || localStorage.getItem('aippt_prev_session_id');

            // 检查messages中是否有加载消息
            if (messages && Array.isArray(messages)) {
                const hasLoadingMessage = messages.some(
                    (msg) =>
                        msg.loading === true ||
                        (msg.content && typeof msg.content === 'object' && msg.content.ongoing_status === '0') ||
                        (typeof msg.content === 'string' && msg.content.includes('正在生成'))
                );

                if (hasLoadingMessage) {
                    setAipptLoading(true);
                    setShowPauseIcon(true);
                } else {
                    setAipptLoading(false);
                    setShowPauseIcon(false);
                }
            } else {
                setAipptLoading(false);
                setShowPauseIcon(false);
            }
        } else {
            setShowPauseIcon(false);
            setIsResearchTask(false);
            setIsPauseClickable(false); // 🔥 重置可点击状态
            setAipptLoading(false); // 🔥 重置AIPPT加载状态
        }
    }, [sessionId, isDeerFlow, isAippt, messages]);

    // 🔥 新增：监听localStorage变化，实时同步canPause和isClickable状态
    useEffect(() => {
        if (!sessionId || !isDeerFlow) return;

        const handleStorageChange = () => {
            const pauseFlag = getSessionPauseFlag(sessionId);

            // 🔥 新增：同时读取isClickable状态
            let clickableFlag = false;
            try {
                const historySessions = JSON.parse(localStorage.getItem('grt_history_sessions') || '[]');
                const sessionData = historySessions.find((item) => (typeof item === 'object' ? item.sessionId === sessionId : item === sessionId));
                clickableFlag = sessionData?.isClickable || false;
            } catch (error) {
                console.error('🔥 读取isClickable状态失败:', error);
            }
            setShowPauseIcon(pauseFlag);
            setIsPauseClickable(clickableFlag); // 🔥 同步更新isClickable状态
        };

        // 监听storage事件（跨标签页变化）
        window.addEventListener('storage', handleStorageChange);

        // 定时检查localStorage变化（同一标签页内的变化storage事件监听不到）
        const checkInterval = setInterval(() => {
            const currentPauseFlag = getSessionPauseFlag(sessionId);

            // 🔥 新增：同时检查isClickable状态
            let currentClickableFlag = false;
            try {
                const historySessions = JSON.parse(localStorage.getItem('grt_history_sessions') || '[]');
                const sessionData = historySessions.find((item) => (typeof item === 'object' ? item.sessionId === sessionId : item === sessionId));
                currentClickableFlag = sessionData?.isClickable || false;
            } catch (error) {
                console.error('🔥 定时检查isClickable状态失败:', error);
            }

            // 检查状态是否有变化
            if (currentPauseFlag !== showPauseIcon || currentClickableFlag !== isPauseClickable) {
                setShowPauseIcon(currentPauseFlag);
                setIsPauseClickable(currentClickableFlag); // 🔥 同步更新isClickable状态
            }
        }, 1000); // 每1秒检查一次

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            clearInterval(checkInterval);
        };
    }, [sessionId, isDeerFlow, showPauseIcon, isPauseClickable]);

    // 🔥 新增：组件卸载时清理定时器
    useEffect(() => {
        return () => {
            if (submitTimeoutRef.current) {
                clearTimeout(submitTimeoutRef.current);
            }
        };
    }, []);

    // 🔥 处理暂停按钮点击
    const handleStop = () => {
        // 🔥 新增：检查暂停按钮是否可点击
        if (!isPauseClickable) {
            return; // 不可点击时直接返回，不执行任何操作
        }

        // 🔥 先停止本地的历史轮询，确保定时器立即被清除，避免在调用停止 API/方法时仍有轮询
        if (stopHistoryPolling && sessionId) {
            try {
                stopHistoryPolling(sessionId);
            } catch (error) {
                console.error('🔥 停止历史记录轮询失败:', error);
            }
        } 
        // 然后调用 DeerFlow 停止 API（直接从 API 引入，不再通过 props 传入）
        if (sessionId) {
            try {
                apiHandleDeerFlowStop({ thread_id: sessionId });
            } catch (error) {
                console.error('🔥 调用 DeerFlow 停止 API 失败:', error);
            }
        }

        if (onStop) {
            onStop();
        }

        // 🔥 新增：停止后重置暂停图标状态
        setShowPauseIcon(false);
        setIsResearchTask(false); // 🔥 重置研究任务标记

        // 🔥 修复：存储停止状态到localStorage，确保页面刷新后能正确显示停止状态
        if (activeReportId) {
            setUserStoppedState(activeReportId);
        }

        // 派发停止事件
        if (activeReportId) {
            window.dispatchEvent(
                new CustomEvent('deerflow-stop', {
                    detail: {
                        reportId: activeReportId,
                        userStopped: true
                    }
                })
            );

            // 🔥 同时派发report-state-change事件，确保UI组件能立即响应
            window.dispatchEvent(
                new CustomEvent('report-state-change', {
                    detail: {
                        reportId: activeReportId,
                        uniqueReportId: activeReportId,
                        status: 'stopped',
                        userStopped: true
                    }
                })
            );
        } else {
            console.warn('🔥 activeReportId 不存在，无法派发停止事件');
        }
    };

    return {
        // 状态
        showPauseIcon,
        setShowPauseIcon,
        isResearchTask,
        setIsResearchTask,
        isPauseClickable,
        setIsPauseClickable,
        aipptLoading,
        setAipptLoading,
        isSubmitting,
        setIsSubmitting,
        
        // 方法
        handleStop,
        
        // Refs
        lastSubmitTimeRef,
        submitTimeoutRef
    };
};

